import type { Position } from 'geojson';

/**
 * Modèle géologique d'un tir en blocs, construit à partir de ce qui a été relevé dans ses trous :
 * lithologies (en pourcentage de la longueur du trou) ou drill logs (en mètres).
 *
 * Chaque trou occupe sa cellule de la vue de dessus (Voronoï découpé au contour du tir), empilée
 * en tranches horizontales : une tranche prend la catégorie majoritaire relevée par le trou à
 * cette altitude. Un trou sans relevé laisse sa colonne vide, comme sa cellule reste sans couleur
 * en vue de dessus.
 *
 * Drill logs : relevés le long du trou, ils suivent son inclinaison. Les cellules sont recalculées
 * à chaque limite de tranche, là où passent les trous à cette altitude, et chaque bloc tend ses
 * facettes latérales entre sa cellule du bas et sa cellule du haut. Les facettes sont construites
 * tranche par tranche pour toutes les colonnes à la fois, de sorte que deux voisins partagent
 * exactement leurs facettes communes — y compris quand les voisinages changent d'une altitude à
 * l'autre — et deux blocs superposés exactement leur contour : les colonnes suivent leurs trous en
 * pans lisses, sans jour ni chevauchement. Lithologies : colonnes verticales sur les cellules de la
 * vue de dessus.
 */

export interface Vec2 { x: number; y: number }
export interface Vec3 extends Vec2 { z: number }

/** Tranche d'un trou : une catégorie (lithologie ou drill log) entre deux profondeurs. */
export interface GeologyInterval {
  id: number;
  from: number;
  to: number;
}

/**
 * Lithologies : profondeurs en pourcentage (0–100) de la longueur du trou.
 * Drill logs : profondeurs en mètres depuis la tête.
 */
export type GeologyKind = 'lithology' | 'drillLog';

/** Position d'un trou (identifiant de sa colonne) dans la projection du site. */
export interface CellSite {
  id: string;
  x: number;
  y: number;
}

/** Ce que la vue 3D d'un tir doit construire, posé par la légende de l'onglet Trous. */
export interface GeologyModel {
  kind: GeologyKind;
  /** Tranches de chaque trou (publicGuid). Une nouvelle Map donne un nouveau modèle à construire. */
  holes: ReadonlyMap<string, readonly GeologyInterval[]>;
  /** Cellule de chaque trou en vue de dessus, dans la projection du site : un anneau par morceau. */
  cells: ReadonlyMap<string, { rings: readonly Position[][] }>;
  /**
   * Drill logs : cellules des trous placés en `sites` (là où ils passent à une altitude donnée),
   * calculées comme celles de la vue de dessus. Sans elle, les colonnes restent verticales.
   */
  cellsAt?: (sites: readonly CellSite[]) => ReadonlyMap<string, { rings: readonly Position[][] }>;
  /** Couleur de chaque catégorie. */
  colors: ReadonlyMap<number, string>;
  /** Catégories masquées depuis la légende : retirées de l'affichage sans reconstruire le modèle. */
  hidden: ReadonlySet<number>;
}

/** Un trou dans le repère de la scène : sa trajectoire de la tête au pied et ses tranches. */
export interface HoleTrack {
  path: Vec3[];
  /**
   * Longueur déclarée du trou (m), à laquelle se rapportent les profondeurs relevées. Elle peut
   * différer de la longueur de la trajectoire déviée : les profondeurs y sont ramenées au prorata,
   * comme les charges.
   */
  length: number;
  intervals: readonly GeologyInterval[];
}

export type Rings = readonly (readonly Vec2[])[];

/**
 * Une colonne : une cellule de la vue de dessus et les trous qui l'occupent — un seul en général,
 * plusieurs quand des trous partagent une tête (la vue de dessus leur donne alors la même cellule).
 */
export interface HoleColumn {
  /** Identifiant de la colonne, le même que celui de ses sites dans `cellsAtLevel`. */
  key: string;
  holes: readonly HoleTrack[];
  /** Cellule de la vue de dessus, dans le repère de la scène. */
  rings: Rings;
}

/**
 * Facette latérale d'un bloc : un triangle dans la hauteur de sa tranche, sommets en sens
 * trigonométrique vus de l'extérieur du bloc (sa normale sort du bloc). Deux blocs voisins qui
 * partagent une facette en ont la même clé.
 */
export interface SideFacet {
  points: readonly Vec3[];
  key: string;
}

/** Un bloc du modèle : une colonne entre deux altitudes, d'une seule catégorie. */
export interface GeologyBlock {
  id: number;
  column: string;
  bottom: number;
  top: number;
  /** Facettes latérales de chaque morceau de cellule, dans l'ordre de son contour, sur toute la hauteur de la tranche. */
  sides: readonly (readonly SideFacet[])[];
  /** Contour de chaque morceau à `bottom` puis à `top` : le dessous et le dessus du bloc. */
  bottomRings: Rings;
  topRings: Rings;
}

/** Hauteur d'une tranche (m). */
export const LAYER_HEIGHT = 1;

/** Couleur d'une catégorie sans couleur dans le référentiel du site, en 3D comme dans la légende. */
export const GEOLOGY_FALLBACK_COLOR = '#9a9a9a';

/** Longueur ou hauteur négligeable (m) : en dessous, c'est un reste d'arrondi, pas un relevé. */
const EPSILON = 1e-6;

/** Nombre maximal de redécoupages d'une tranche en deux (voir buildBlocks) : jusqu'à 1/8 de tranche. */
const MAX_SPLITS = 3;

/** Écart minimal (m) entre deux colonnes à une même altitude (voir positionsAt). */
const MIN_SPACING = 0.01;

/**
 * Blocs du modèle : pour chaque colonne, une tranche par altitude où ses trous ont relevé quelque
 * chose. Chaque tranche relevée est découpée aux segments de la trajectoire puis aux limites des
 * tranches d'altitude : chaque tranche d'altitude reçoit la longueur exacte de chaque catégorie qui
 * la traverse, si courte soit-elle, et prend la plus longue.
 *
 * @param cellsAtLevel Drill logs : cellules des colonnes à l'altitude `level × layer`, recalculées là
 *                     où passent les trous (voir positionsAt), du côté `side` de cette altitude : 1 pour
 *                     la tranche du dessus, -1 pour celle du dessous. Les deux ne diffèrent que là où
 *                     des trous se croisent. Un bloc tend ses facettes entre les cellules des deux
 *                     limites de sa tranche ; une colonne sans cellule à l'une d'elles n'y a pas de
 *                     bloc. Sans elle, chaque colonne garde sa cellule de la vue de dessus.
 * @param tracks       Drill logs : trajectoires des trous de chaque colonne, celles dont les positions
 *                     donnent les cellules de `cellsAtLevel`. Une tranche où deux d'entre elles se
 *                     croisent y est coupée (voir crossingLevels).
 */
export function buildBlocks(
  columns: Iterable<HoleColumn>,
  kind: GeologyKind,
  layer = LAYER_HEIGHT,
  cellsAtLevel?: (level: number, side: 1 | -1) => ReadonlyMap<string, Rings>,
  tracks?: ReadonlyMap<string, readonly (readonly Vec3[])[]>,
): GeologyBlock[] {
  const blocks: GeologyBlock[] = [];
  // Cellules de chaque côté de chaque altitude ; les mêmes des deux côtés (le cas courant) sont un
  // seul objet, auquel les demi-tranches du dessous et du dessus se raccordent (voir chainBands).
  const computed = new Map<string, { rings: ReadonlyMap<string, Rings>; cells: Map<string, LabelledRing[]> }>();
  const labelledAt = (level: number, side: 1 | -1): Map<string, LabelledRing[]> => {
    let entry = computed.get(`${level}|${side}`);
    if (!entry) {
      const rings = cellsAtLevel!(level, side);
      const other = computed.get(`${level}|${-side}`);
      entry = { rings, cells: other && sameCells(other.rings, rings) ? other.cells : labelEdges(rings) };
      computed.set(`${level}|${side}`, entry);
    }
    return entry.cells;
  };
  // Altitude où couper une tranche en deux : le milieu, ou à côté si des trous s'y croisent, pour
  // que les deux demi-tranches partagent leurs cellules à cette altitude.
  const middleOf = (low: number, high: number): number => {
    for (const share of [1 / 2, 7 / 16, 9 / 16, 3 / 8, 5 / 8]) {
      const level = low + (high - low) * share;
      if (labelledAt(level, 1) === labelledAt(level, -1)) return level;
    }
    return (low + high) / 2;
  };
  // Une tranche dont les colonnes changent de voisins d'une façon qu'une seule bascule n'explique pas
  // est redécoupée en deux, jusqu'à MAX_SPLITS fois, pour isoler des bascules simples.
  const chainsBetween = (low: number, high: number, depth: number): Map<string, Chain[]> => {
    const report = { unresolved: false };
    const chains = bandSides(labelledAt(low, 1), labelledAt(high, -1), low * layer, high * layer, report);
    if (!report.unresolved || depth >= MAX_SPLITS) return chains;
    const middle = middleOf(low, high);
    return chainBands(chainsBetween(low, middle, depth + 1), chainsBetween(middle, high, depth + 1));
  };
  // Morceaux de chaque colonne dans une tranche, chacun avec ses facettes. La tranche est d'abord
  // coupée là où des trous se croisent ; une colonne dont la cellule y change d'un coup (voir
  // positionsAt) y a deux blocs, chacun fermé par son dessus et son dessous. Les autres n'en ont qu'un.
  const partsByBand = new Map<number, Map<string, BandPart[]>>();
  const bandPartsAt = (band: number): Map<string, BandPart[]> => {
    let parts = partsByBand.get(band);
    if (!parts) {
      const crossings = tracks ? crossingLevels(tracks, band * layer, (band + 1) * layer, layer).map(z => z / layer) : [];
      const levels = [band, ...crossings, band + 1];
      const segments = levels.slice(1).map((high, index) => chainsBetween(levels[index], high, 0));
      parts = new Map<string, BandPart[]>();
      for (const column of new Set(segments.flatMap(segment => [...segment.keys()]))) {
        const list: BandPart[] = [];
        segments.forEach((segment, index) => {
          const sides = (segment.get(column) ?? []).map(chain => chain.facets);
          const previous = list[list.length - 1];
          const below = computed.get(`${levels[index]}|-1`)?.rings.get(column);
          const above = computed.get(`${levels[index]}|1`)?.rings.get(column);
          if (previous && below && above && sameRingsExactly(below, above)) {
            previous.high = levels[index + 1];
            previous.sides.push(...sides);
          } else {
            list.push({ low: levels[index], high: levels[index + 1], sides });
          }
        });
        parts.set(column, list);
      }
      partsByBand.set(band, parts);
    }
    return parts;
  };

  for (const column of columns) {
    if (!column.rings.length) continue;
    const lengthsByBand = new Map<number, Map<number, number>>();
    let highest = -Infinity;
    let lowest = Infinity;

    for (const hole of column.holes) {
      const path = hole.path;
      const lengths = cumulativeLengths(path);
      const pathLength = lengths[lengths.length - 1] ?? 0;
      // Trou de longueur inconnue : la légende ne le compte pas, le modèle ne le dessine pas.
      if (!(pathLength > 0) || !(hole.length > 0)) continue;

      // Les blocs s'arrêtent aux têtes et aux pieds des trous : le dessus du modèle suit les têtes.
      for (const point of path) {
        highest = Math.max(highest, point.z);
        lowest = Math.min(lowest, point.z);
      }

      const toMetres = metresPerUnit(kind, hole.length);
      const toPath = pathLength / hole.length;
      for (const interval of hole.intervals) {
        // Coupée à la longueur déclarée (comme les parts de la légende), puis placée sur la trajectoire.
        const from = clamp(Math.min(interval.from, interval.to) * toMetres, hole.length) * toPath;
        const to = clamp(Math.max(interval.from, interval.to) * toMetres, hole.length) * toPath;
        // Tranche vide, ou entièrement hors du trou une fois ramenée à sa longueur : rien de relevé.
        if (!(to - from > EPSILON)) continue;

        for (let i = 1; i < path.length; i++) {
          const start = Math.max(from, lengths[i - 1]);
          const end = Math.min(to, lengths[i]);
          if (!(end - start > EPSILON)) continue;
          const span = lengths[i] - lengths[i - 1];
          const zAt = (distanceFromCollar: number): number =>
            path[i - 1].z + (path[i].z - path[i - 1].z) * ((distanceFromCollar - lengths[i - 1]) / span);
          addToBands(lengthsByBand, interval.id, zAt(start), zAt(end), end - start, layer);
        }
      }
    }

    for (const [band, cell] of lengthsByBand) {
      const parts: readonly BandPart[] = cellsAtLevel
        ? bandPartsAt(band).get(column.key) ?? []
        : [{ low: band, high: band + 1, sides: column.rings.map(ring => verticalFacets(ring, band * layer, (band + 1) * layer)) }];
      for (const { low, high, sides } of parts) {
        const bottom = Math.max(low * layer, lowest);
        const top = Math.min(high * layer, highest);
        if (!(top - bottom > EPSILON) || !sides.length) continue;
        blocks.push({
          id: majority(cell),
          column: column.key,
          bottom,
          top,
          sides,
          bottomRings: capRings(sides, bottom),
          topRings: capRings(sides, top),
        });
      }
    }
  }
  return blocks;
}

/** Morceau d'une colonne dans une tranche, entre deux altitudes (en tranches) : les facettes de chacun de ses morceaux de cellule. */
interface BandPart {
  low: number;
  high: number;
  sides: SideFacet[][];
}

/**
 * Position horizontale de chaque colonne à l'altitude `z` : celle de ses trous, en moyenne quand
 * ils sont plusieurs. Toutes les colonnes ont une position, même quand leur trou n'atteint pas
 * cette altitude (voir trackPositionAt) : sans cela, l'altitude des têtes les plus hautes ou des
 * pieds les plus profonds ne compterait que quelques trous, dont les cellules s'étendraient sur la
 * place de tous les autres. Un trou d'un seul point (sans trajectoire) reste à sa tête.
 *
 * Des trous inclinés qui se croisent mettent des colonnes au même endroit à l'altitude du
 * croisement, où elles se partageraient une même cellule. Elles sont alors placées comme juste à
 * côté, du côté `side` de cette altitude (1 : juste au-dessus, -1 : juste en dessous ; à défaut de
 * l'autre côté, puis à leur tête) : leur disposition autour de leur centre commun, ramenée à l'échelle
 * où les deux plus proches sont à MIN_SPACING. Leurs cellules sont ainsi coupées entre elles comme
 * dans la tranche de ce côté, sans se recouvrir ; d'un côté à l'autre du croisement, elles échangent
 * leur place. Des trajectoires confondues partout sont réparties autour de leur centre.
 */
export function positionsAt(
  columns: ReadonlyMap<string, readonly (readonly Vec3[])[]>,
  z: number,
  layer = LAYER_HEIGHT,
  side: 1 | -1 = 1,
): Map<string, Vec2> {
  const positions = averagePositions(columns, z, layer);
  for (const cluster of coincident(positions)) {
    const center = centroid(cluster.map(key => positions.get(key)!));
    const tracksOfCluster = new Map(cluster.map(key => [key, columns.get(key)!] as const));
    // Disposition des colonnes juste à côté, du côté demandé d'abord. Une disposition très irrégulière
    // (deux colonnes presque confondues devant l'étendue) les éloignerait trop une fois mise à
    // l'échelle : on cherche plus loin, et à défaut on garde la plus régulière.
    let best: { offsets: Vec2[]; closest: number; spread: number } | null = null;
    for (const nearby of [z + side * layer / 64, z + side * layer / 8, z - side * layer / 64, z - side * layer / 8, Infinity]) {
      const around = averagePositions(tracksOfCluster, nearby, layer);
      const middle = centroid([...around.values()]);
      const offsets = cluster.map(key => ({ x: around.get(key)!.x - middle.x, y: around.get(key)!.y - middle.y }));
      const closest = Math.min(...offsets.flatMap((a, i) => offsets.slice(i + 1).map(b => Math.hypot(a.x - b.x, a.y - b.y))));
      const spread = Math.max(...offsets.map(offset => Math.hypot(offset.x, offset.y)));
      if (!(closest > EPSILON)) continue;
      if (!best || closest / spread > best.closest / best.spread) best = { offsets, closest, spread };
      if (closest / spread > 0.01) break;
    }
    if (best) {
      const scale = MIN_SPACING / best.closest;
      cluster.forEach((key, index) => positions.set(key, { x: center.x + best!.offsets[index].x * scale, y: center.y + best!.offsets[index].y * scale }));
    } else {
      [...cluster].sort().forEach((key, index) => {
        const angle = (2 * Math.PI * index) / cluster.length;
        positions.set(key, { x: center.x + Math.cos(angle) * MIN_SPACING, y: center.y + Math.sin(angle) * MIN_SPACING });
      });
    }
  }
  return positions;
}

/**
 * Altitudes (m) strictement entre `zb` et `zt` où deux colonnes se croisent : leurs trous y passent à
 * moins de MIN_SPACING l'un de l'autre. Les trajectoires déviées sont des lignes brisées : les
 * positions vont en ligne droite d'un sommet de trajectoire à l'autre, et chacun de ces intervalles
 * est vérifié à part — un trou peut croiser son voisin puis revenir dans la même tranche. De part et
 * d'autre d'un croisement, les colonnes échangent leur place (voir positionsAt).
 */
function crossingLevels(tracks: ReadonlyMap<string, readonly (readonly Vec3[])[]>, zb: number, zt: number, layer: number): number[] {
  const breaks = new Set([zb, zt]);
  for (const paths of tracks.values()) {
    for (const path of paths) {
      for (const point of path) if (point.z > zb && point.z < zt) breaks.add(point.z);
    }
  }
  const altitudes = [...breaks].sort((a, b) => a - b);
  const levels = new Set<number>();
  let low = averagePositions(tracks, altitudes[0], layer);
  for (let span = 1; span < altitudes.length; span++) {
    const [za, zc] = [altitudes[span - 1], altitudes[span]];
    const high = averagePositions(tracks, zc, layer);
    const keys = [...low.keys()].filter(key => high.has(key));
    for (let i = 0; i < keys.length; i++) {
      for (let j = i + 1; j < keys.length; j++) {
        const [a0, a1, b0, b1] = [low.get(keys[i])!, high.get(keys[i])!, low.get(keys[j])!, high.get(keys[j])!];
        const start = { x: a0.x - b0.x, y: a0.y - b0.y };
        const move = { x: a1.x - b1.x - start.x, y: a1.y - b1.y - start.y };
        const length2 = move.x * move.x + move.y * move.y;
        // Trop loin l'un de l'autre pour se rejoindre dans cet intervalle, ou immobiles l'un par rapport à l'autre.
        if (!(length2 > 0) || Math.hypot(start.x, start.y) > Math.sqrt(length2) + MIN_SPACING) continue;
        const f = Math.min(1, Math.max(0, -(start.x * move.x + start.y * move.y) / length2));
        const z = za + f * (zc - za);
        if (z > zb + EPSILON && z < zt - EPSILON && Math.hypot(start.x + f * move.x, start.y + f * move.y) < MIN_SPACING) {
          levels.add(Math.round(z * 1e6) / 1e6);
        }
      }
    }
    low = high;
  }
  return [...levels].sort((a, b) => a - b);
}

/** Position moyenne des trous de chaque colonne à l'altitude `z`. */
function averagePositions(columns: ReadonlyMap<string, readonly (readonly Vec3[])[]>, z: number, layer: number): Map<string, Vec2> {
  const positions = new Map<string, Vec2>();
  for (const [key, paths] of columns) {
    let x = 0;
    let y = 0;
    let count = 0;
    for (const path of paths) {
      if (!path.length) continue;
      const point = trackPositionAt(path, z, layer);
      x += point.x;
      y += point.y;
      count++;
    }
    if (count) positions.set(key, { x: x / count, y: y / count });
  }
  return positions;
}

/** Groupes de colonnes à moins de MIN_SPACING les unes des autres (de proche en proche). */
function coincident(positions: ReadonlyMap<string, Vec2>): string[][] {
  const keys = [...positions.keys()];
  const parent = keys.map((_, index) => index);
  const find = (index: number): number => parent[index] === index ? index : (parent[index] = find(parent[index]));
  // Grille de pas MIN_SPACING : deux colonnes trop proches sont dans la même case ou dans une case voisine.
  const grid = new Map<string, number[]>();
  const cellOf = (point: Vec2) => [Math.floor(point.x / MIN_SPACING), Math.floor(point.y / MIN_SPACING)];
  keys.forEach((key, index) => {
    const point = positions.get(key)!;
    const [i, j] = cellOf(point);
    for (let di = -1; di <= 1; di++) {
      for (let dj = -1; dj <= 1; dj++) {
        for (const other of grid.get(`${i + di},${j + dj}`) ?? []) {
          if (distance2(point, positions.get(keys[other])!) < MIN_SPACING * MIN_SPACING) parent[find(index)] = find(other);
        }
      }
    }
    const cell = grid.get(`${i},${j}`) ?? [];
    cell.push(index);
    grid.set(`${i},${j}`, cell);
  });
  const clusters = new Map<number, string[]>();
  keys.forEach((key, index) => {
    const cluster = clusters.get(find(index)) ?? [];
    cluster.push(key);
    clusters.set(find(index), cluster);
  });
  return [...clusters.values()].filter(cluster => cluster.length > 1);
}

/**
 * Position horizontale du trou à l'altitude `z`. Au-delà de sa tête ou de son pied, le trou est
 * prolongé en ligne droite jusqu'à la limite de tranche qui suit, puis ne bouge plus. Les cellules
 * de la tranche où il commence ou finit sont tendues entre ces deux limites : à l'altitude de sa
 * tête ou de son pied, elles retombent ainsi sur lui, au lieu d'un point entre lui et sa position
 * figée à la limite.
 */
function trackPositionAt(path: readonly Vec3[], z: number, layer: number): Vec2 {
  const zs = path.map(point => point.z);
  const high = Math.max(...zs);
  const low = Math.min(...zs);
  if (!(high - low > EPSILON)) return path[0];
  if (z > high) return beyondEnd(path, zs.indexOf(high), Math.min(z, Math.ceil(high / layer - EPSILON) * layer));
  if (z < low) return beyondEnd(path, zs.indexOf(low), Math.max(z, Math.floor(low / layer + EPSILON) * layer));
  return horizontalPositionAt(path, z);
}

/** Position à l'altitude `z` du prolongement en ligne droite du trou au-delà de son extrémité `end` (premier ou dernier point). */
function beyondEnd(path: readonly Vec3[], end: number, z: number): Vec2 {
  if (end !== 0 && end !== path.length - 1) return path[end];
  const step = end === 0 ? 1 : -1;
  const tip = path[end];
  for (let i = end + step; i >= 0 && i < path.length; i += step) {
    const other = path[i];
    if (Math.abs(other.z - tip.z) > EPSILON) {
      const t = (z - tip.z) / (other.z - tip.z);
      return { x: tip.x + (other.x - tip.x) * t, y: tip.y + (other.y - tip.y) * t };
    }
  }
  return tip;
}

/** Position horizontale du trou à l'altitude `z`, en suivant sa trajectoire depuis la tête. */
function horizontalPositionAt(path: readonly Vec3[], z: number): Vec2 {
  if (z >= path[0].z) return path[0];
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    const low = Math.min(a.z, b.z);
    const high = Math.max(a.z, b.z);
    // Segment horizontal : il ne fixe pas la position à une altitude donnée.
    if (high - low < EPSILON || z < low || z > high) continue;
    const t = (z - a.z) / (b.z - a.z);
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  }
  return path[path.length - 1];
}

// ── Facettes latérales ───────────────────────────────────────────────────────

/** Contour d'une cellule dont chaque arête (i → i + 1) porte la colonne voisine qu'elle sépare, ou '' au bord. */
interface LabelledRing {
  points: readonly Vec2[];
  labels: readonly string[];
}

/** Marque chaque arête des cellules d'une altitude avec la colonne qui la partage (voir edgeKey). */
function labelEdges(cells: ReadonlyMap<string, Rings>): Map<string, LabelledRing[]> {
  const owners = new Map<string, string[]>();
  for (const [column, rings] of cells) {
    for (const ring of rings) {
      ring.forEach((point, index) => {
        const key = edgeKey(point, ring[(index + 1) % ring.length]);
        const list = owners.get(key) ?? [];
        list.push(column);
        owners.set(key, list);
      });
    }
  }

  const labelled = new Map<string, LabelledRing[]>();
  for (const [column, rings] of cells) {
    labelled.set(column, rings.map(ring => {
      const points = counterClockwise(ring);
      return {
        points,
        labels: points.map((point, index) =>
          owners.get(edgeKey(point, points[(index + 1) % points.length]))?.find(owner => owner !== column) ?? ''),
      };
    }));
  }
  return labelled;
}

/** Deux colonnes voisines, dans un ordre fixe. */
function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * Facettes latérales de toutes les colonnes dans une tranche, entre leurs cellules du bas (`zb`) et
 * du haut (`zt`).
 *
 * Une arête entre deux colonnes qui existe aux deux altitudes (voir matchEdges) donne un
 * quadrilatère, que ses deux colonnes construisent à l'identique. Entre deux de ces arêtes, le
 * contour d'une colonne peut changer : des trous d'inclinaisons différentes font disparaître ou
 * apparaître des arêtes de voisinage, une arête sort par le bord du tir, un bord apparaît… Les
 * morceaux de contour qui changent et se touchent forment un même changement, raccordé en éventail
 * à un seul point X au milieu de la tranche (voir joinChanges) : chaque facette tendue vers X est
 * construite à l'identique par les deux colonnes qui la partagent, quel que soit le nombre de
 * colonnes en jeu. Le bord du tir qui borde la colonne aux deux altitudes est raccordé en bande de
 * triangles.
 *
 * Un morceau de cellule qui n'existe qu'à l'une des deux altitudes (une cellule qui se sépare en
 * deux contre un contour concave, deux morceaux qui se rejoignent, une cellule écrasée entre deux
 * trous qui se croisent) est tout entier un changement : son contour est tendu vers le point X qu'il
 * partage avec les changements voisins, en cône. Les dessus et dessous de la tranche restent
 * exactement les cellules calculées. Ce raccord n'est qu'approché ; il est signalé dans `report`,
 * comme les changements qui touchent beaucoup de colonnes, pour que la tranche soit redécoupée et
 * qu'il ne couvre qu'une fine épaisseur.
 */
function bandSides(
  bottomCells: ReadonlyMap<string, LabelledRing[]>,
  topCells: ReadonlyMap<string, LabelledRing[]>,
  zb: number,
  zt: number,
  report: { unresolved: boolean },
): Map<string, Chain[]> {
  const chains = new Map<string, Chain[]>();
  const partner = new Map<LabelledRing, LabelledRing>();
  const lofted: { bottom: LabelledRing; top: LabelledRing; facets: SideFacet[] }[] = [];
  const changes: Change[] = [];
  for (const column of new Set([...bottomCells.keys(), ...topCells.keys()])) {
    chains.set(column, matchPieces(bottomCells.get(column) ?? [], topCells.get(column) ?? []).map(([bottom, top]) => {
      if (!bottom || !top) {
        report.unresolved = true;
        const facets: SideFacet[] = [];
        changes.push({ bottom: whole(bottom), top: whole(top), closed: true, facets });
        return { bottom, top, facets };
      }
      partner.set(bottom, top);
      partner.set(top, bottom);
      const chain = { bottom, top, facets: [] as SideFacet[] };
      lofted.push(chain);
      return chain;
    }));
  }

  const matches = matchEdges(bottomCells, topCells, partner, lofted, report);
  for (const { bottom, top, facets } of lofted) loft(bottom, top, zb, zt, matches, facets, changes, report);
  joinChanges(changes, zb, zt, report);
  return chains;
}

/**
 * Morceau de cellule suivi d'une altitude à l'autre : ses contours du bas et du haut (les mêmes
 * objets que les cellules de ces altitudes, null pour un cône) et ses facettes entre les deux.
 */
interface Chain {
  bottom: LabelledRing | null;
  top: LabelledRing | null;
  facets: SideFacet[];
}

/**
 * Raccorde les morceaux de deux demi-tranches superposées : un morceau du bas continue dans celui
 * du haut qui part de la même cellule à l'altitude commune. Un morceau du haut qui ne prolonge
 * rien (un cône qui naît dans la demi-tranche du haut) est gardé tel quel.
 */
function chainBands(lower: ReadonlyMap<string, Chain[]>, upper: ReadonlyMap<string, Chain[]>): Map<string, Chain[]> {
  const chained = new Map<string, Chain[]>();
  for (const column of new Set([...lower.keys(), ...upper.keys()])) {
    const above = [...(upper.get(column) ?? [])];
    const continued = (lower.get(column) ?? []).map(chain => {
      const index = chain.top ? above.findIndex(candidate => candidate.bottom === chain.top) : -1;
      if (index < 0) return chain;
      const [next] = above.splice(index, 1);
      return { bottom: chain.bottom, top: next.top, facets: [...chain.facets, ...next.facets] };
    });
    chained.set(column, [...continued, ...above]);
  }
  return chained;
}

/**
 * Apparie les morceaux d'une cellule entre le bas et le haut de la tranche — une cellule coupée en
 * plusieurs morceaux par un contour concave : les paires qui bordent le plus des mêmes voisins, puis
 * les plus proches, d'abord, chaque morceau au plus une fois. Les morceaux restés sans pendant (une
 * cellule qui se sépare ou se rejoint) sont rendus seuls, avec null de l'autre côté.
 */
function matchPieces(bottom: LabelledRing[], top: LabelledRing[]): [LabelledRing | null, LabelledRing | null][] {
  if (bottom.length === 1 && top.length === 1) return [[bottom[0], top[0]]];
  const candidates = bottom.flatMap(low => top.map(high => ({
    low,
    high,
    score: low.labels.filter(label => label && high.labels.includes(label)).length * 1e6 - distance2(centroid(low.points), centroid(high.points)),
  })));
  candidates.sort((a, b) => b.score - a.score);
  const pairs: [LabelledRing | null, LabelledRing | null][] = [];
  const used = new Set<LabelledRing>();
  for (const { low, high } of candidates) {
    if (used.has(low) || used.has(high)) continue;
    used.add(low);
    used.add(high);
    pairs.push([low, high]);
  }
  for (const low of bottom) if (!used.has(low)) pairs.push([low, null]);
  for (const high of top) if (!used.has(high)) pairs.push([null, high]);
  return pairs;
}

/**
 * Apparie, d'une altitude à l'autre, les arêtes entre deux mêmes colonnes (clé de l'arête du bas →
 * clé de l'arête du haut, voir edgeKey) : chaque arête du bas va à l'arête du haut la plus proche
 * entre les mêmes colonnes et les mêmes morceaux de cellule (voir matchPieces), chacune au plus
 * une fois. Deux colonnes séparées par plusieurs arêtes (un contour concave entre elles) voient
 * ainsi chacune apparier avec la sienne. L'appariement est fait une fois pour les deux colonnes de
 * l'arête ; une colonne qui rencontrerait ses arêtes appariées dans un autre ordre en haut qu'en bas
 * en perd, pour toutes les colonnes, jusqu'à ce que l'ordre soit le même partout.
 */
function matchEdges(
  bottomCells: ReadonlyMap<string, LabelledRing[]>,
  topCells: ReadonlyMap<string, LabelledRing[]>,
  partner: ReadonlyMap<LabelledRing, LabelledRing>,
  lofted: readonly { bottom: LabelledRing; top: LabelledRing }[],
  report: { unresolved: boolean },
): Map<string, string> {
  interface Edge { key: string; middle: Vec2; owners: Map<string, LabelledRing> }
  const edgesOf = (cells: ReadonlyMap<string, LabelledRing[]>): Map<string, Map<string, Edge>> => {
    const byPair = new Map<string, Map<string, Edge>>();
    for (const [column, rings] of cells) {
      for (const ring of rings) {
        ring.labels.forEach((label, index) => {
          if (!label) return;
          const a = ring.points[index];
          const b = ring.points[(index + 1) % ring.points.length];
          const key = edgeKey(a, b);
          const pair = pairKey(column, label);
          const edges = byPair.get(pair) ?? new Map<string, Edge>();
          const edge = edges.get(key) ?? { key, middle: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, owners: new Map<string, LabelledRing>() };
          edge.owners.set(column, ring);
          edges.set(key, edge);
          byPair.set(pair, edges);
        });
      }
    }
    return byPair;
  };

  const below = edgesOf(bottomCells);
  const above = edgesOf(topCells);
  const matches = new Map<string, string>();
  for (const [pair, lows] of below) {
    const highs = above.get(pair);
    if (!highs) continue;
    const candidates: { low: Edge; high: Edge; gap: number }[] = [];
    for (const low of lows.values()) {
      for (const high of highs.values()) {
        if ([...low.owners].every(([column, ring]) => partner.get(ring) === high.owners.get(column))) {
          candidates.push({ low, high, gap: distance2(low.middle, high.middle) });
        }
      }
    }
    candidates.sort((a, b) => a.gap - b.gap);
    const used = new Set<Edge>();
    for (const { low, high } of candidates) {
      if (used.has(low) || used.has(high)) continue;
      used.add(low);
      used.add(high);
      matches.set(low.key, high.key);
    }
  }

  for (let dropped = true; dropped;) {
    dropped = false;
    for (const { bottom, top } of lofted) {
      const order = matchedOrder(bottom, top, matches);
      const kept = cyclicIncreasing(order.map(([, it]) => it), top.points.length);
      order.forEach(([ib], rank) => {
        if (kept.has(rank)) return;
        matches.delete(edgeKey(bottom.points[ib], bottom.points[(ib + 1) % bottom.points.length]));
        dropped = true;
        report.unresolved = true;
      });
    }
  }
  return matches;
}

/** Arêtes appariées d'un morceau, dans l'ordre de son contour du bas : [indice en bas, indice en haut]. */
function matchedOrder(bottom: LabelledRing, top: LabelledRing, matches: ReadonlyMap<string, string>): [number, number][] {
  const topIndex = new Map<string, number>();
  top.labels.forEach((label, index) => {
    if (label) topIndex.set(edgeKey(top.points[index], top.points[(index + 1) % top.points.length]), index);
  });
  const order: [number, number][] = [];
  bottom.labels.forEach((label, index) => {
    const match = label ? matches.get(edgeKey(bottom.points[index], bottom.points[(index + 1) % bottom.points.length])) : undefined;
    const it = match === undefined ? undefined : topIndex.get(match);
    if (it !== undefined) order.push([index, it]);
  });
  return order;
}

/**
 * Rangs de la plus longue sous-suite de `values`, prise en boucle, qui fait au plus un tour de 0 à
 * `modulo` en croissant : les arêtes appariées qu'un contour du haut rencontre dans le même ordre
 * que celui du bas.
 */
function cyclicIncreasing(values: readonly number[], modulo: number): Set<number> {
  const descents = values.filter((value, rank) => value > values[(rank + 1) % values.length]).length;
  if (descents <= 1) return new Set(values.keys());
  let best: number[] = [];
  values.forEach((first, start) => {
    const ranks = values.map((_, offset) => (start + offset) % values.length);
    const unrolled = ranks.map(rank => (values[rank] - first + modulo) % modulo);
    const lengths = unrolled.map(() => 1);
    const previous = unrolled.map(() => -1);
    for (let i = 1; i < unrolled.length; i++) {
      for (let j = 0; j < i; j++) {
        if (unrolled[j] < unrolled[i] && lengths[j] + 1 > lengths[i]) {
          lengths[i] = lengths[j] + 1;
          previous[i] = j;
        }
      }
    }
    const kept: number[] = [];
    for (let i = lengths.indexOf(Math.max(...lengths)); i >= 0; i = previous[i]) kept.push(ranks[i]);
    if (kept.length > best.length) best = kept;
  });
  return new Set(best);
}

/** Morceau de contour : ses sommets, l'étiquette de chaque arête, et les sommets posés sur le bord du tir. */
interface Stretch {
  points: Vec2[];
  labels: string[];
  onEdge: boolean[];
}

/** Morceau de contour qui change entre le bas et le haut d'une tranche (voir loft), et les facettes de sa colonne. */
interface Change {
  bottom: Stretch;
  top: Stretch;
  /** Vrai quand tout le contour change : le bas et le haut sont deux boucles (ou rien), sans côté entre elles. */
  closed: boolean;
  facets: SideFacet[];
}

/**
 * Facettes d'un morceau de cellule entre le bas et le haut de la tranche : un quadrilatère par arête
 * appariée (voir matchEdges), et entre deux d'entre elles, le morceau de contour qui change. Le bord
 * du tir qui ouvre ou ferme ce morceau aux deux altitudes est raccordé en bande de triangles ; le
 * reste est rangé dans `changes`, pour être tendu vers son point X (voir joinChanges).
 */
function loft(
  bottom: LabelledRing,
  top: LabelledRing,
  zb: number,
  zt: number,
  matches: ReadonlyMap<string, string>,
  facets: SideFacet[],
  changes: Change[],
  report: { unresolved: boolean },
): void {
  const lift = (point: Vec2, z: number): Vec3 => ({ x: point.x, y: point.y, z });
  const m = bottom.points.length;
  const n = top.points.length;
  const order = matchedOrder(bottom, top, matches);
  if (!order.length) {
    // Aucune arête appariée : une colonne seule, que le bord du tir entoure aux deux altitudes, ou dont tout le contour change.
    if (![...bottom.labels, ...top.labels].some(label => label)) facets.push(...zipClosed(bottom.points, top.points, zb, zt));
    else changes.push({ bottom: whole(bottom), top: whole(top), closed: true, facets });
    return;
  }

  order.forEach(([ib, it], rank) => {
    facets.push(...facet([lift(bottom.points[ib], zb), lift(bottom.points[(ib + 1) % m], zb), lift(top.points[(it + 1) % n], zt), lift(top.points[it], zt)]));

    // Jusqu'à l'arête appariée suivante (ou le retour à celle-ci).
    const [nextB, nextT] = order[(rank + 1) % order.length];
    const gapB = stretch(bottom, ib + 1, (nextB - ib - 1 + m) % m);
    const gapT = stretch(top, it + 1, (nextT - it - 1 + n) % n);
    const edgesB = gapB.labels.length;
    const edgesT = gapT.labels.length;

    // Le bord du tir, en tête ou en queue du morceau aux deux altitudes : la colonne y touche le bord
    // en haut comme en bas, rien n'y change pour ses voisins. Si ce n'est pas sur le même côté du
    // contour, la limite entre les deux colonnes passe un coin dans la tranche et le quadrilatère
    // apparié le coupe : signalé, pour que la tranche soit redécoupée.
    const opens = edgesB > 0 && edgesT > 0 && !gapB.labels[0] && !gapT.labels[0];
    const closes = edgesB > 0 && edgesT > 0 && !gapB.labels[edgesB - 1] && !gapT.labels[edgesT - 1];
    if (opens && !sameSide(gapB.points[0], gapB.points[1], gapT.points[0], gapT.points[1])) report.unresolved = true;
    if (closes && !sameSide(gapB.points[edgesB - 1], gapB.points[edgesB], gapT.points[edgesT - 1], gapT.points[edgesT])) report.unresolved = true;
    const b0 = opens ? leadingEdges(gapB.labels, 0) : 0;
    const t0 = opens ? leadingEdges(gapT.labels, 0) : 0;
    const b1 = closes ? edgesB - trailingEdges(gapB.labels, b0) : edgesB;
    const t1 = closes ? edgesT - trailingEdges(gapT.labels, t0) : edgesT;
    if (b0 || t0) facets.push(...zipOpen(gapB.points.slice(0, b0 + 1), gapT.points.slice(0, t0 + 1), zb, zt));
    if (b1 < edgesB || t1 < edgesT) facets.push(...zipOpen(gapB.points.slice(b1), gapT.points.slice(t1), zb, zt));
    if (b1 > b0 || t1 > t0) changes.push({ bottom: part(gapB, b0, b1), top: part(gapT, t0, t1), closed: false, facets });
  });
}

/** Morceau du contour `ring` de `edges` arêtes, à partir du sommet `start`. */
function stretch(ring: LabelledRing, start: number, edges: number): Stretch {
  const size = ring.points.length;
  const result: Stretch = { points: [], labels: [], onEdge: [] };
  for (let k = 0; k <= edges; k++) {
    const index = (start + k) % size;
    result.points.push(ring.points[index]);
    result.onEdge.push(!ring.labels[index] || !ring.labels[(index + size - 1) % size]);
    if (k < edges) result.labels.push(ring.labels[index]);
  }
  return result;
}

/** Tout le contour `ring`, bouclé sur son premier sommet ; rien sans contour. */
function whole(ring: LabelledRing | null): Stretch {
  return ring ? stretch(ring, 0, ring.points.length) : { points: [], labels: [], onEdge: [] };
}

/** Arêtes `from` à `to` d'un morceau de contour, avec leurs sommets. */
function part(line: Stretch, from: number, to: number): Stretch {
  return { points: line.points.slice(from, to + 1), labels: line.labels.slice(from, to), onEdge: line.onEdge.slice(from, to + 1) };
}

/** Nombre d'arêtes du bord du tir ('') au début de `labels`, à partir de `from`. */
function leadingEdges(labels: readonly string[], from: number): number {
  let count = 0;
  while (from + count < labels.length && !labels[from + count]) count++;
  return count;
}

/** Nombre d'arêtes du bord du tir ('') à la fin de `labels`, sans descendre sous `from`. */
function trailingEdges(labels: readonly string[], from: number): number {
  let count = 0;
  while (labels.length - count > from && !labels[labels.length - 1 - count]) count++;
  return count;
}

/**
 * Raccorde les morceaux de contour qui changent (voir loft). Ceux qui ont un sommet en commun, à la
 * même altitude, forment un même changement : tous sont tendus en éventail vers un même point X, au
 * milieu de la tranche, si bien que chaque facette partagée par deux colonnes est la même des deux
 * côtés. Un changement qui touche le bord du tir (une arête qui y sort, un bord qui apparaît) a son
 * X sur le bord, que ses facettes extérieures suivent.
 */
function joinChanges(changes: readonly Change[], zb: number, zt: number, report: { unresolved: boolean }): void {
  const parent = changes.map((_, index) => index);
  const find = (index: number): number => parent[index] === index ? index : (parent[index] = find(parent[index]));
  const owner = new Map<string, number>();
  changes.forEach((change, index) => {
    for (const [line, level] of [[change.bottom, 'b'], [change.top, 't']] as const) {
      for (const point of line.points) {
        const key = level + pointKey(point);
        const other = owner.get(key);
        if (other === undefined) owner.set(key, index);
        else parent[find(index)] = find(other);
      }
    }
  });

  const groups = new Map<number, Change[]>();
  changes.forEach((change, index) => {
    const group = groups.get(find(index)) ?? [];
    group.push(change);
    groups.set(find(index), group);
  });
  for (const group of groups.values()) {
    // Une bascule simple met en jeu quatre colonnes ; au-delà, X est loin de certaines d'entre elles.
    // Un changement le long de deux côtés du contour (il y passe un coin) a son X hors du bord.
    if (group.length > 4 || crossesCorner(group)) report.unresolved = true;
    const x = junction(group, (zb + zt) / 2);
    for (const change of group) fan(change, x, zb, zt);
  }
}

/** Vrai si les arêtes du bord du tir d'un changement ne sont pas toutes sur le même côté du contour. */
function crossesCorner(group: readonly Change[]): boolean {
  const edges = group.flatMap(change => [change.bottom, change.top].flatMap(line =>
    line.labels.flatMap((label, k) => label ? [] : [[line.points[k], line.points[k + 1]] as const])));
  return edges.some(([a, b]) => !sameSide(edges[0][0], edges[0][1], a, b));
}

/**
 * Point X d'un changement : au centre de ses sommets du bas et de ses sommets du haut, à parts
 * égales, à l'altitude `zm`. S'il touche le bord du tir, seuls comptent ses sommets posés sur le bord.
 */
function junction(group: readonly Change[], zm: number): Vec3 {
  const touchesEdge = group.some(change => change.bottom.labels.includes('') || change.top.labels.includes(''));
  const centers = (['bottom', 'top'] as const).map(level => {
    const points = new Map<string, Vec2>();
    for (const change of group) {
      const line = change[level];
      line.points.forEach((point, index) => {
        if (!touchesEdge || line.onEdge[index]) points.set(pointKey(point), point);
      });
    }
    return [...points.values()];
  }).filter(points => points.length).map(points => centroid(points));
  const center = centroid(centers);
  return { x: center.x, y: center.y, z: zm };
}

/**
 * Facettes d'un morceau de contour qui change, en éventail vers `x` : son tour — le bas dans le sens
 * du contour, puis le haut à rebours — dans le sens des facettes de la colonne (voir loft).
 */
function fan(change: Change, x: Vec3, zb: number, zt: number): void {
  const bottom = change.bottom.points.map(point => ({ x: point.x, y: point.y, z: zb }));
  const top = change.top.points.map(point => ({ x: point.x, y: point.y, z: zt }));
  const edges: [Vec3, Vec3][] = [];
  if (change.closed) {
    for (let i = 0; i + 1 < bottom.length; i++) edges.push([bottom[i], bottom[i + 1]]);
    for (let j = top.length - 1; j > 0; j--) edges.push([top[j], top[j - 1]]);
  } else {
    const loop = [...bottom, ...[...top].reverse()];
    loop.forEach((point, index) => edges.push([point, loop[(index + 1) % loop.length]]));
  }
  for (const [from, to] of edges) {
    if (from.z === to.z && distance2(from, to) <= 1e-12) continue;
    change.facets.push(...facet([from, to, x]));
  }
}

/** Point au millimètre, comme edgeKey. */
function pointKey(point: Vec2): string {
  return `${Math.round(point.x * 1000)},${Math.round(point.y * 1000)}`;
}

/** Facettes d'une cellule qui ne change pas d'une altitude à l'autre : colonne verticale. */
function verticalFacets(ring: readonly Vec2[], zb: number, zt: number): SideFacet[] {
  const points = counterClockwise(ring);
  return points.flatMap((point, index) => {
    const next = points[(index + 1) % points.length];
    return facet([
      { x: point.x, y: point.y, z: zb },
      { x: next.x, y: next.y, z: zb },
      { x: next.x, y: next.y, z: zt },
      { x: point.x, y: point.y, z: zt },
    ]);
  });
}

/**
 * Bande de triangles entre deux lignes brisées qui partent et arrivent aux mêmes sommets (bas `low`,
 * haut `high`) : le bord du tir, le même contour décalé d'une altitude à l'autre. Deux arêtes sur le
 * même côté du contour se répondent en quadrilatère, plan. Sinon on avance sur la ligne en retard,
 * dont le côté vient avant dans le contour : chaque triangle reste sur un côté au lieu de couper un
 * coin. À défaut d'indice, on avance selon la part de longueur parcourue.
 */
function zipOpen(low: readonly Vec2[], high: readonly Vec2[], zb: number, zt: number): SideFacet[] {
  const facets: SideFacet[] = [];
  if (!low.length || !high.length) return facets;
  const at = (point: Vec2, z: number): Vec3 => ({ x: point.x, y: point.y, z });
  // Vrai si l'arête k du bas et l'arête l du haut existent et sont sur le même côté du contour.
  const along = (k: number, l: number): boolean =>
    k >= 0 && l >= 0 && k < low.length - 1 && l < high.length - 1 && sameSide(low[k], low[k + 1], high[l], high[l + 1]);
  const lowAt = progress(low);
  const highAt = progress(high);
  let i = 0;
  let j = 0;
  while (i < low.length - 1 || j < high.length - 1) {
    if (along(i, j)) {
      facets.push(...facet([at(low[i], zb), at(low[i + 1], zb), at(high[j + 1], zt), at(high[j], zt)]));
      i++;
      j++;
      continue;
    }
    let advanceLow: boolean;
    if (j >= high.length - 1) advanceLow = true;
    else if (i >= low.length - 1) advanceLow = false;
    else if (low.slice(i + 1, -1).some((_, k) => along(i + 1 + k, j)) || along(i, j - 1)) advanceLow = true;
    else if (high.slice(j + 1, -1).some((_, l) => along(i, j + 1 + l)) || along(i - 1, j)) advanceLow = false;
    else advanceLow = lowAt[i + 1] <= highAt[j + 1];
    if (advanceLow) {
      facets.push(...facet([at(low[i], zb), at(low[i + 1], zb), at(high[j], zt)]));
      i++;
    } else {
      facets.push(...facet([at(low[i], zb), at(high[j + 1], zt), at(high[j], zt)]));
      j++;
    }
  }
  return facets;
}

/**
 * Bande de triangles entre deux contours fermés sans arête partagée (le bord du tir tout autour).
 * Les deux contours repartent d'un même côté du contour — les arêtes de même direction les plus
 * proches, quel que soit le décalage d'une altitude à l'autre — et à défaut de leurs sommets les
 * plus proches.
 */
function zipClosed(low: readonly Vec2[], high: readonly Vec2[], zb: number, zt: number): SideFacet[] {
  const middle = (ring: readonly Vec2[], index: number): Vec2 => {
    const next = ring[(index + 1) % ring.length];
    return { x: (ring[index].x + next.x) / 2, y: (ring[index].y + next.y) / 2 };
  };
  let startLow = 0;
  let startHigh = high.reduce((nearest, point, index) => distance2(point, low[0]) < distance2(high[nearest], low[0]) ? index : nearest, 0);
  let best = Infinity;
  for (let i = 0; i < low.length; i++) {
    for (let j = 0; j < high.length; j++) {
      if (!sameSide(low[i], low[(i + 1) % low.length], high[j], high[(j + 1) % high.length])) continue;
      const gap = distance2(middle(low, i), middle(high, j));
      if (gap < best) {
        best = gap;
        startLow = i;
        startHigh = j;
      }
    }
  }
  const rotatedLow = [...low.slice(startLow), ...low.slice(0, startLow)];
  const rotatedHigh = [...high.slice(startHigh), ...high.slice(0, startHigh)];
  return zipOpen([...rotatedLow, rotatedLow[0]], [...rotatedHigh, rotatedHigh[0]], zb, zt);
}

/** Vrai si les arêtes a → b et c → d vont dans la même direction : le même côté du contour, décalé d'une altitude à l'autre. */
function sameSide(a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean {
  const u = { x: b.x - a.x, y: b.y - a.y };
  const v = { x: d.x - c.x, y: d.y - c.y };
  const lengths = Math.hypot(u.x, u.y) * Math.hypot(v.x, v.y);
  return lengths > EPSILON * EPSILON && Math.abs(u.x * v.y - u.y * v.x) < 1e-6 * lengths && u.x * v.x + u.y * v.y > 0;
}

/** Part de la longueur parcourue à chaque sommet d'une ligne brisée (0 → 1) ; à défaut de longueur, part des sommets. */
function progress(line: readonly Vec2[]): number[] {
  const lengths = [0];
  for (let i = 1; i < line.length; i++) lengths.push(lengths[i - 1] + Math.sqrt(distance2(line[i - 1], line[i])));
  const total = lengths[lengths.length - 1];
  return lengths.map((length, index) => total > EPSILON ? length / total : index / Math.max(1, line.length - 1));
}

/**
 * Facettes d'un triangle ou d'un quadrilatère, avec leur clé : leurs sommets au millimètre, sans
 * ordre, de sorte que leurs deux propriétaires les reconnaissent. Un quadrilatère tendu entre deux
 * arêtes qui tournent d'une altitude à l'autre n'est pas plan : il est coupé en deux triangles par
 * la diagonale qui part de son sommet de plus petite clé, la même pour les deux voisins qui le
 * partagent — deux diagonales différentes laisseraient un coin vide entre eux.
 */
function facet(points: Vec3[]): SideFacet[] {
  const keyOf = (point: Vec3) => `${Math.round(point.x * 1000)},${Math.round(point.y * 1000)},${Math.round(point.z * 1000)}`;
  const make = (triangle: Vec3[]): SideFacet => ({ points: triangle, key: triangle.map(keyOf).sort().join('|') });
  if (points.length !== 4) return [make(points)];
  // Deux sommets au même millimètre (une arête de moins d'un millimètre) sont départagés par leurs
  // coordonnées exactes, sans quoi chaque voisin prendrait le premier qu'il rencontre.
  const order = (a: Vec3, b: Vec3) => keyOf(a) < keyOf(b) ? -1 : keyOf(a) > keyOf(b) ? 1 : a.x - b.x || a.y - b.y || a.z - b.z;
  const smallest = points.indexOf([...points].sort(order)[0]);
  const [q0, q1, q2, q3] = [0, 1, 2, 3].map(offset => points[(smallest + offset) % 4]);
  return [make([q0, q1, q2]), make([q0, q2, q3])];
}

/**
 * Contours de chaque morceau à l'altitude `z` : les coupes de ses facettes, raccordées bout à bout
 * quel que soit l'ordre des facettes. Chaque coupe est tournée par la normale de sa facette, l'intérieur
 * à gauche : les contours sortent en sens trigonométrique. Une coupe en double (l'arête commune de deux
 * facettes posée dans le plan, à la limite entre deux demi-tranches) ne compte qu'une fois, et les
 * sommets alignés au milieu d'un côté (coupe d'une diagonale) disparaissent.
 */
function capRings(sides: readonly (readonly SideFacet[])[], z: number): Vec2[][] {
  const near = (a: Vec2, b: Vec2) => distance2(a, b) <= 1e-12;
  return sides.flatMap(facets => {
    const segments: [Vec2, Vec2][] = [];
    for (const item of facets) {
      const segment = sliceFacet(item, z);
      if (!segment) continue;
      const { x: nx, y: ny } = facetNormal(item);
      const [a, b] = segment;
      const directed: [Vec2, Vec2] = (b.x - a.x) * -ny + (b.y - a.y) * nx >= 0 ? [a, b] : [b, a];
      if (!segments.some(([start, end]) => near(start, directed[0]) && near(end, directed[1]))) segments.push(directed);
    }

    const rings: Vec2[][] = [];
    const used = new Set<number>();
    segments.forEach(([start, end], first) => {
      if (used.has(first)) return;
      used.add(first);
      const ring = [start];
      let current = end;
      while (!near(current, start)) {
        const next = segments.findIndex(([from], index) => !used.has(index) && near(from, current));
        if (next < 0) break;
        used.add(next);
        ring.push(current);
        current = segments[next][1];
      }
      if (!near(current, start)) ring.push(current);
      const corners = withoutAlignedPoints(ring);
      if (corners.length >= 3) rings.push(signedArea(corners) < 0 ? corners.reverse() : corners);
    });
    return rings;
  });
}

/** Contour sans ses sommets alignés avec leurs deux voisins, ni ses sommets en double. */
function withoutAlignedPoints(ring: readonly Vec2[]): Vec2[] {
  return ring.filter((point, index) => {
    const previous = ring[(index + ring.length - 1) % ring.length];
    const next = ring[(index + 1) % ring.length];
    const cross = (point.x - previous.x) * (next.y - point.y) - (point.y - previous.y) * (next.x - point.x);
    return distance2(previous, point) > 1e-12 && Math.abs(cross) > 1e-9 * Math.sqrt(distance2(previous, point) * distance2(point, next));
  });
}

/** Normale d'une facette (non normée), tournée vers l'extérieur de son bloc. */
export function facetNormal(item: SideFacet): Vec3 {
  const [p0, p1, p2] = item.points;
  return {
    x: (p1.y - p0.y) * (p2.z - p0.z) - (p1.z - p0.z) * (p2.y - p0.y),
    y: (p1.z - p0.z) * (p2.x - p0.x) - (p1.x - p0.x) * (p2.z - p0.z),
    z: (p1.x - p0.x) * (p2.y - p0.y) - (p1.y - p0.y) * (p2.x - p0.x),
  };
}

/** Coupe d'une facette à l'altitude `z` : le segment où elle traverse ce plan, ou null. */
export function sliceFacet(item: SideFacet, z: number): [Vec2, Vec2] | null {
  const points: Vec2[] = [];
  const add = (point: Vec2) => {
    if (!points.some(other => distance2(other, point) <= 1e-12)) points.push(point);
  };
  const vertices = item.points;
  vertices.forEach((a, index) => {
    const b = vertices[(index + 1) % vertices.length];
    if (Math.abs(a.z - z) < EPSILON) add(a);
    if ((a.z - z) * (b.z - z) < 0) {
      const t = (z - a.z) / (b.z - a.z);
      add({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  });
  return points.length >= 2 ? [points[0], points[points.length - 1]] : null;
}

/** Partie d'une facette entre les altitudes `low` et `high`, sommets toujours tournés vers l'extérieur. */
export function clipFacet(item: SideFacet, low: number, high: number): Vec3[] {
  const clip = (polygon: Vec3[], inside: (point: Vec3) => boolean, level: number): Vec3[] => {
    const result: Vec3[] = [];
    polygon.forEach((current, index) => {
      const next = polygon[(index + 1) % polygon.length];
      if (inside(current)) result.push(current);
      if (inside(current) !== inside(next)) {
        const t = (level - current.z) / (next.z - current.z);
        result.push({ x: current.x + (next.x - current.x) * t, y: current.y + (next.y - current.y) * t, z: level });
      }
    });
    return result;
  };
  const above = clip([...item.points], point => point.z >= low - EPSILON, low);
  return clip(above, point => point.z <= high + EPSILON, high);
}

// ── Faces extérieures ────────────────────────────────────────────────────────

/** Hauteurs `[bas, haut]` (m) d'un morceau de mur à dessiner. */
export type ZRange = readonly [bottom: number, top: number];

/**
 * Faces à dessiner d'un bloc : dessus, dessous, et pour chaque morceau, pour chaque facette
 * latérale, les hauteurs de mur à dessiner — aucune si le bloc voisin couvre tout le mur.
 */
export interface BlockFaces {
  top: boolean;
  bottom: boolean;
  walls: ZRange[][][];
}

/** Arête de cellule identifiée par ses deux extrémités au millimètre, dans un sens ou dans l'autre : deux cellules voisines la partagent. */
export function edgeKey(a: Vec2, b: Vec2): string {
  const pa = pointKey(a);
  const pb = pointKey(b);
  return pa < pb ? `${pa}|${pb}` : `${pb}|${pa}`;
}

/**
 * Faces extérieures des blocs affichés. Un bloc n'a pas de dessus (ou de dessous) quand un bloc de
 * sa colonne est collé contre lui avec exactement le même contour (même cellule à la limite de
 * tranche, ou même cellule de la vue de dessus). Ses murs perdent la hauteur couverte par les blocs
 * voisins qui partagent la même facette (ses sommets, altitude comprise : elle n'existe que dans sa
 * tranche). La transparence ne s'empile ainsi pas d'un bloc à l'autre, et la coupe du curseur comme
 * les couches masquées laissent des faces fermées.
 */
export function exteriorFaces(blocks: readonly GeologyBlock[]): BlockFaces[] {
  const at = (column: string, z: number): string => `${column}|${Math.round(z * 1e6)}`;
  const byBottom = new Map<string, GeologyBlock>();
  const byTop = new Map<string, GeologyBlock>();
  const owners = new Map<string, GeologyBlock[]>();
  for (const block of blocks) {
    byBottom.set(at(block.column, block.bottom), block);
    byTop.set(at(block.column, block.top), block);
    for (const facets of block.sides) {
      for (const item of facets) {
        const list = owners.get(item.key) ?? [];
        list.push(block);
        owners.set(item.key, list);
      }
    }
  }

  return blocks.map(block => {
    const above = byBottom.get(at(block.column, block.top));
    const below = byTop.get(at(block.column, block.bottom));
    const walls = block.sides.map(facets => facets.map(item => {
      let spans: ZRange[] = [[block.bottom, block.top]];
      for (const neighbour of owners.get(item.key) ?? []) {
        if (neighbour.column !== block.column) spans = withoutRange(spans, neighbour.bottom, neighbour.top);
      }
      return spans;
    }));
    return {
      top: !(above && sameRings(block.topRings, above.bottomRings)),
      bottom: !(below && sameRings(block.bottomRings, below.topRings)),
      walls,
    };
  });
}

/** Vrai si deux jeux de cellules sont les mêmes, sommet pour sommet. */
function sameCells(a: ReadonlyMap<string, Rings>, b: ReadonlyMap<string, Rings>): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const [key, rings] of a) {
    const other = b.get(key);
    if (!other || !sameRingsExactly(rings, other)) return false;
  }
  return true;
}

/** Vrai si deux cellules sont les mêmes, sommet pour sommet. */
function sameRingsExactly(a: Rings, b: Rings): boolean {
  return a === b || (a.length === b.length && a.every((ring, r) => ring.length === b[r].length
    && ring.every((point, i) => point.x === b[r][i].x && point.y === b[r][i].y)));
}

/** Vrai si deux contours sont les mêmes, au millimètre : deux blocs superposés qui s'y raccordent exactement. */
function sameRings(a: Rings, b: Rings): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  const tolerance = 1e-3;
  const same = (ring: readonly Vec2[], other: readonly Vec2[]) => ring.length === other.length && ring.every(point =>
    other.some(candidate => Math.abs(point.x - candidate.x) < tolerance && Math.abs(point.y - candidate.y) < tolerance));
  return a.every(ring => b.some(other => same(ring, other)));
}

/** Morceaux de `spans` hors de `[low, high]`, sans les restes négligeables. */
function withoutRange(spans: readonly ZRange[], low: number, high: number): ZRange[] {
  const kept: ZRange[] = [];
  for (const [bottom, top] of spans) {
    if (high <= bottom + EPSILON || low >= top - EPSILON) {
      kept.push([bottom, top]);
      continue;
    }
    if (low - bottom > EPSILON) kept.push([bottom, low]);
    if (top - high > EPSILON) kept.push([high, top]);
  }
  return kept;
}

// ── Relevés ──────────────────────────────────────────────────────────────────

/**
 * Répartit `length` mètres d'une catégorie, relevés en ligne droite de l'altitude `za` à `zb`,
 * entre les tranches d'altitude traversées, au prorata de la hauteur parcourue dans chacune.
 */
function addToBands(lengthsByBand: Map<number, Map<number, number>>, id: number, za: number, zb: number, length: number, layer: number): void {
  const add = (band: number, part: number): void => {
    const cell = lengthsByBand.get(band) ?? new Map<number, number>();
    cell.set(id, (cell.get(id) ?? 0) + part);
    lengthsByBand.set(band, cell);
  };

  const low = Math.min(za, zb);
  const high = Math.max(za, zb);
  // Morceau horizontal : tout dans la tranche de son altitude.
  if (high - low < EPSILON) {
    add(Math.floor((low + high) / 2 / layer), length);
    return;
  }
  for (let band = Math.floor(low / layer); band * layer < high; band++) {
    const overlap = Math.min(high, (band + 1) * layer) - Math.max(low, band * layer);
    // Un morceau qui finit pile sur une limite ne déborde pas d'un reste d'arrondi sur la tranche voisine.
    if (overlap > EPSILON) add(band, (length * overlap) / (high - low));
  }
}

/**
 * Facteur qui convertit les profondeurs des tranches d'un trou de `holeLength` mètres en mètres
 * depuis la tête. Lithologies : pourcentages 0–100, contrat de l'API (GraphReading.API,
 * HoleGeologyType) — une tranche à 0–1 % reste 1 % du trou, pas le trou entier.
 */
export function metresPerUnit(kind: GeologyKind, holeLength: number): number {
  return kind === 'lithology' ? holeLength / 100 : 1;
}

/** Couleur saisie en config : `#rrggbb` ou `#rgb`, avec ou sans `#` ; `fallback` si illisible. */
export function normalizeHexColor(color: string | null | undefined, fallback: string): string {
  const value = color?.trim() ?? '';
  if (/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) return value.startsWith('#') ? value : `#${value}`;
  return fallback;
}

function clamp(value: number, max: number): number {
  return Math.min(max, Math.max(0, Number.isFinite(value) ? value : 0));
}

/** Catégorie couvrant la plus grande longueur de la tranche. */
function majority(cell: Map<number, number>): number {
  let best = 0;
  let bestLength = -1;
  for (const [id, length] of cell) {
    if (length > bestLength) {
      best = id;
      bestLength = length;
    }
  }
  return best;
}

/** Longueur cumulée le long de la trajectoire, du premier point à chacun des suivants. */
function cumulativeLengths(path: readonly Vec3[]): number[] {
  const lengths = path.length ? [0] : [];
  for (let i = 1; i < path.length; i++) lengths.push(lengths[i - 1] + distance(path[i - 1], path[i]));
  return lengths;
}

function distance(a: Vec3, b: Vec3): number {
  return Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
}

function distance2(a: Vec2, b: Vec2): number {
  return (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
}

function centroid(points: readonly Vec2[]): Vec2 {
  const sum = points.reduce((total, point) => ({ x: total.x + point.x, y: total.y + point.y }), { x: 0, y: 0 });
  return { x: sum.x / Math.max(1, points.length), y: sum.y / Math.max(1, points.length) };
}

/** Le contour en sens trigonométrique. */
function counterClockwise(ring: readonly Vec2[]): readonly Vec2[] {
  return signedArea(ring) < 0 ? [...ring].reverse() : ring;
}

function signedArea(ring: readonly Vec2[]): number {
  let area = 0;
  ring.forEach((point, index) => {
    const next = ring[(index + 1) % ring.length];
    area += point.x * next.y - next.x * point.y;
  });
  return area / 2;
}
